/**
 * CareLogDetail Component
 *
 * Full view of a single care log with edit mode toggle and AI extracted data display.
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { CareLogForm, CareLogTag } from './CareLogForm';
import {
  Calendar, Clock, Edit2, ArrowLeft, AlertTriangle, Loader2,
  AlertCircle, Brain, Pill, Activity, Heart, MapPin, Timer
} from 'lucide-react';

// Processing status for AI analysis
type ProcessingStatus = 'pending' | 'processing' | 'completed' | 'failed';

// Severity levels
type SeverityLevel = 'routine' | 'notable' | 'concerning' | 'urgent';

// Sentiment types
type Sentiment = 'positive' | 'neutral' | 'negative' | 'concerning';

// Extracted behavior
interface ExtractedBehavior {
  behavior: string;
  count?: number;
  intensity?: 'mild' | 'moderate' | 'severe';
  triggers?: string[];
}

// Extracted medication
interface ExtractedMedication {
  medicationName: string;
  dosage?: string;
  timeTaken?: string;
}

// Extracted symptom
interface ExtractedSymptom {
  symptom: string;
  severity?: 'mild' | 'moderate' | 'severe';
}

// Extracted incident
interface ExtractedIncident {
  type: string;
  description: string;
  severity?: 'mild' | 'moderate' | 'severe';
}

// Extracted intervention
interface ExtractedIntervention {
  intervention: string;
  effectiveness?: 'low' | 'medium' | 'high';
  duration?: string;
}

// Extracted data from AI
interface ExtractedData {
  behaviors?: ExtractedBehavior[];
  mood?: string;
  moodTransition?: { starting?: string; ending?: string };
  medicationsTaken?: ExtractedMedication[];
  medicationsRefused?: string[];
  symptoms?: ExtractedSymptom[];
  incidents?: ExtractedIncident[];
  interventions?: ExtractedIntervention[];
  location?: string;
  duration?: string;
  timeOfDay?: string;
}

interface CareLog {
  logId: string;
  caseFileId: string;
  userId: string;
  logText: string;
  logDate: string;
  logTimestamp: { _seconds: number; _nanoseconds: number };
  tags: string[];
  inputType: 'text' | 'voice';
  createdAt: { _seconds: number; _nanoseconds: number };
  updatedAt: { _seconds: number; _nanoseconds: number };
  createdBy: string;
  lastModifiedBy?: string;
  // Phase 2D: AI processing fields
  processingStatus?: ProcessingStatus;
  extractedData?: ExtractedData;
  sentiment?: Sentiment;
  severityLevel?: SeverityLevel;
  summary?: string;
  flagsForReview?: boolean;
  flagReason?: string;
  processingError?: string;
}

interface GetCareLogResponse {
  success: boolean;
  careLog?: CareLog;
  error?: string;
}

interface UpdateCareLogResponse {
  success: boolean;
  careLog?: CareLog;
  error?: string;
}

interface CareLogDetailProps {
  caseFileId: string;
  logId: string;
  onBack?: () => void;
  onUpdate?: () => void;
}

const TAG_COLORS: Record<string, string> = {
  medical: 'bg-red-100 text-red-800',
  school: 'bg-blue-100 text-blue-800',
  insurance: 'bg-green-100 text-green-800',
  admin: 'bg-gray-100 text-gray-800',
  behavior: 'bg-purple-100 text-purple-800',
  medication: 'bg-orange-100 text-orange-800',
};

const SEVERITY_STYLES: Record<SeverityLevel, { bg: string; text: string; border: string }> = {
  routine: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-500' },
  notable: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-500' },
  concerning: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-500' },
  urgent: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-500' },
};

const SENTIMENT_LABELS: Record<Sentiment, { icon: string; label: string; color: string }> = {
  positive: { icon: '😊', label: 'Positive', color: 'text-green-600' },
  neutral: { icon: '😐', label: 'Neutral', color: 'text-gray-600' },
  negative: { icon: '😟', label: 'Negative', color: 'text-yellow-600' },
  concerning: { icon: '⚠️', label: 'Concerning', color: 'text-orange-600' },
};

const INTENSITY_STYLES: Record<string, string> = {
  mild: 'bg-green-100 text-green-800',
  moderate: 'bg-yellow-100 text-yellow-800',
  severe: 'bg-red-100 text-red-800',
};

const EFFECTIVENESS_STYLES: Record<string, string> = {
  low: 'bg-red-100 text-red-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-green-100 text-green-800',
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTimestamp(timestamp: { _seconds: number }): string {
  if (!timestamp?._seconds) return '';
  const date = new Date(timestamp._seconds * 1000);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function CareLogDetail({
  caseFileId,
  logId,
  onBack,
  onUpdate,
}: CareLogDetailProps): JSX.Element {
  const [careLog, setCareLog] = useState<CareLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchCareLog = async () => {
    setLoading(true);
    setError(null);

    try {
      const getCareLog = httpsCallable<
        { caseFileId: string; logId: string },
        GetCareLogResponse
      >(functions, 'getCareLog');

      const result = await getCareLog({ caseFileId, logId });

      if (result.data.success && result.data.careLog) {
        setCareLog(result.data.careLog);
      } else {
        setError(result.data.error || 'Failed to load care log');
      }
    } catch (err) {
      console.error('Error fetching care log:', err);
      setError(err instanceof Error ? err.message : 'Failed to load care log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCareLog();
  }, [caseFileId, logId]);

  const handleUpdate = async (data: { logText: string; logDate: string; tags: CareLogTag[] }) => {
    setIsSaving(true);

    try {
      const updateCareLog = httpsCallable<
        { caseFileId: string; logId: string; logText?: string; logDate?: string; tags?: string[] },
        UpdateCareLogResponse
      >(functions, 'updateCareLog');

      const result = await updateCareLog({
        caseFileId,
        logId,
        logText: data.logText,
        logDate: data.logDate,
        tags: data.tags,
      });

      if (result.data.success && result.data.careLog) {
        setCareLog(result.data.careLog);
        setIsEditing(false);
        onUpdate?.();
      } else {
        throw new Error(result.data.error || 'Failed to update care log');
      }
    } catch (err) {
      console.error('Error updating care log:', err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-48 bg-soft-neutral rounded" />
            <div className="h-4 w-32 bg-soft-neutral rounded" />
            <div className="h-32 bg-soft-neutral rounded" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="alert alert-error flex justify-between items-center">
            <span>Error: {error}</span>
            <button onClick={fetchCareLog} className="btn-ghost btn-sm">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!careLog) {
    return (
      <div className="card">
        <div className="card-body text-center py-8">
          <p className="text-muted">Care log not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="btn-ghost btn-sm p-1">
              <ArrowLeft size={20} />
            </button>
          )}
          <div>
            <h3 className="font-semibold text-ink flex items-center gap-2">
              <Calendar size={16} />
              {formatDate(careLog.logDate)}
            </h3>
          </div>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="btn-ghost btn-sm flex items-center gap-1"
          >
            <Edit2 size={16} />
            Edit
          </button>
        )}
      </div>

      <div className="card-body">
        {isEditing ? (
          <CareLogForm
            initialText={careLog.logText}
            initialDate={careLog.logDate}
            initialTags={careLog.tags as CareLogTag[]}
            onSubmit={handleUpdate}
            onCancel={() => setIsEditing(false)}
            submitLabel="Save Changes"
            isLoading={isSaving}
          />
        ) : (
          <div className="space-y-4">
            {/* Severity & Sentiment Banner */}
            {(careLog.severityLevel || careLog.sentiment || careLog.flagsForReview) && (
              <div className={`rounded-lg p-3 ${
                careLog.severityLevel ? SEVERITY_STYLES[careLog.severityLevel].bg : 'bg-gray-50'
              }`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    {careLog.severityLevel && (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${
                        SEVERITY_STYLES[careLog.severityLevel].bg
                      } ${SEVERITY_STYLES[careLog.severityLevel].text} ${
                        SEVERITY_STYLES[careLog.severityLevel].border
                      }`}>
                        {careLog.severityLevel.charAt(0).toUpperCase() + careLog.severityLevel.slice(1)}
                      </span>
                    )}
                    {careLog.sentiment && (
                      <span className={`flex items-center gap-1 ${SENTIMENT_LABELS[careLog.sentiment].color}`}>
                        <span>{SENTIMENT_LABELS[careLog.sentiment].icon}</span>
                        <span className="text-sm">{SENTIMENT_LABELS[careLog.sentiment].label}</span>
                      </span>
                    )}
                  </div>
                  {careLog.flagsForReview && (
                    <div className="flex items-center gap-1 text-orange-600">
                      <AlertTriangle size={16} />
                      <span className="text-sm font-medium">Flagged for Review</span>
                    </div>
                  )}
                </div>
                {careLog.flagReason && (
                  <p className="text-sm text-muted mt-2">{careLog.flagReason}</p>
                )}
              </div>
            )}

            {/* Processing Status */}
            {careLog.processingStatus === 'processing' && (
              <div className="flex items-center gap-2 text-sm text-muted bg-blue-50 px-3 py-2 rounded">
                <Loader2 size={16} className="animate-spin" />
                AI is analyzing this entry...
              </div>
            )}
            {careLog.processingStatus === 'failed' && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                <AlertCircle size={16} />
                AI processing failed{careLog.processingError ? `: ${careLog.processingError}` : ''}
              </div>
            )}

            {/* AI Summary */}
            {careLog.summary && (
              <div className="bg-stone/30 px-4 py-3 rounded-lg">
                <div className="flex items-center gap-2 text-sm font-medium text-ink mb-1">
                  <Brain size={16} />
                  AI Summary
                </div>
                <p className="text-ink">{careLog.summary}</p>
              </div>
            )}

            {/* Tags */}
            {careLog.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {careLog.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      TAG_COLORS[tag] || 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Log text */}
            <div>
              <h4 className="text-sm font-medium text-muted mb-2">Full Entry</h4>
              <p className="text-ink whitespace-pre-wrap leading-relaxed">
                {careLog.logText}
              </p>
            </div>

            {/* AI Extracted Information */}
            {careLog.extractedData && careLog.processingStatus === 'completed' && (
              <div className="border-t border-soft-neutral pt-4">
                <h4 className="text-sm font-medium text-muted mb-3 flex items-center gap-2">
                  <Brain size={16} />
                  AI Extracted Information
                </h4>
                <div className="space-y-4">
                  {/* Behaviors */}
                  {careLog.extractedData.behaviors && careLog.extractedData.behaviors.length > 0 && (
                    <div>
                      <h5 className="text-xs font-medium text-muted mb-2 flex items-center gap-1">
                        <Activity size={14} />
                        Behaviors
                      </h5>
                      <div className="space-y-2">
                        {careLog.extractedData.behaviors.map((b, i) => (
                          <div key={i} className="bg-purple-50 rounded px-3 py-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-purple-800">{b.behavior}</span>
                              {b.count && <span className="text-sm text-purple-600">({b.count}x)</span>}
                              {b.intensity && (
                                <span className={`px-2 py-0.5 rounded text-xs ${INTENSITY_STYLES[b.intensity]}`}>
                                  {b.intensity}
                                </span>
                              )}
                            </div>
                            {b.triggers && b.triggers.length > 0 && (
                              <p className="text-sm text-purple-600 mt-1">
                                Triggers: {b.triggers.join(', ')}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Medications */}
                  {(careLog.extractedData.medicationsTaken?.length || careLog.extractedData.medicationsRefused?.length) && (
                    <div>
                      <h5 className="text-xs font-medium text-muted mb-2 flex items-center gap-1">
                        <Pill size={14} />
                        Medications
                      </h5>
                      <div className="space-y-1">
                        {careLog.extractedData.medicationsTaken?.map((m, i) => (
                          <div key={`taken-${i}`} className="flex items-center gap-2 text-sm bg-green-50 px-3 py-1.5 rounded">
                            <span className="text-green-700 font-medium">{m.medicationName}</span>
                            {m.dosage && <span className="text-green-600">({m.dosage})</span>}
                            {m.timeTaken && <span className="text-green-600">at {m.timeTaken}</span>}
                            <span className="text-green-600 ml-auto">Taken</span>
                          </div>
                        ))}
                        {careLog.extractedData.medicationsRefused?.map((m, i) => (
                          <div key={`refused-${i}`} className="flex items-center gap-2 text-sm bg-red-50 px-3 py-1.5 rounded">
                            <span className="text-red-700 font-medium">{m}</span>
                            <span className="text-red-600 ml-auto">Refused</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Symptoms */}
                  {careLog.extractedData.symptoms && careLog.extractedData.symptoms.length > 0 && (
                    <div>
                      <h5 className="text-xs font-medium text-muted mb-2">Symptoms</h5>
                      <div className="flex flex-wrap gap-2">
                        {careLog.extractedData.symptoms.map((s, i) => (
                          <span key={i} className={`px-2 py-1 rounded text-sm ${
                            s.severity ? INTENSITY_STYLES[s.severity] : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {s.symptom}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Incidents */}
                  {careLog.extractedData.incidents && careLog.extractedData.incidents.length > 0 && (
                    <div>
                      <h5 className="text-xs font-medium text-muted mb-2 flex items-center gap-1">
                        <AlertTriangle size={14} />
                        Incidents
                      </h5>
                      <div className="space-y-2">
                        {careLog.extractedData.incidents.map((inc, i) => (
                          <div key={i} className="bg-orange-50 rounded px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-orange-800">{inc.type}</span>
                              {inc.severity && (
                                <span className={`px-2 py-0.5 rounded text-xs ${INTENSITY_STYLES[inc.severity]}`}>
                                  {inc.severity}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-orange-700 mt-1">{inc.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Interventions */}
                  {careLog.extractedData.interventions && careLog.extractedData.interventions.length > 0 && (
                    <div>
                      <h5 className="text-xs font-medium text-muted mb-2">Interventions</h5>
                      <div className="space-y-1">
                        {careLog.extractedData.interventions.map((int, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm bg-blue-50 px-3 py-1.5 rounded">
                            <span className="text-blue-800 font-medium">{int.intervention}</span>
                            {int.effectiveness && (
                              <span className={`px-2 py-0.5 rounded text-xs ${EFFECTIVENESS_STYLES[int.effectiveness]}`}>
                                {int.effectiveness} effectiveness
                              </span>
                            )}
                            {int.duration && <span className="text-blue-600">({int.duration})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mood */}
                  {(careLog.extractedData.mood || careLog.extractedData.moodTransition) && (
                    <div>
                      <h5 className="text-xs font-medium text-muted mb-2 flex items-center gap-1">
                        <Heart size={14} />
                        Mood
                      </h5>
                      <div className="bg-blue-50 rounded px-3 py-2">
                        {careLog.extractedData.mood && (
                          <p className="text-blue-800">{careLog.extractedData.mood}</p>
                        )}
                        {careLog.extractedData.moodTransition && (
                          <p className="text-sm text-blue-600">
                            {careLog.extractedData.moodTransition.starting && (
                              <span>Started: {careLog.extractedData.moodTransition.starting}</span>
                            )}
                            {careLog.extractedData.moodTransition.starting && careLog.extractedData.moodTransition.ending && ' → '}
                            {careLog.extractedData.moodTransition.ending && (
                              <span>Ended: {careLog.extractedData.moodTransition.ending}</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Location, Duration, Time */}
                  {(careLog.extractedData.location || careLog.extractedData.duration || careLog.extractedData.timeOfDay) && (
                    <div className="flex flex-wrap gap-4 text-sm">
                      {careLog.extractedData.location && (
                        <div className="flex items-center gap-1 text-muted">
                          <MapPin size={14} />
                          {careLog.extractedData.location}
                        </div>
                      )}
                      {careLog.extractedData.duration && (
                        <div className="flex items-center gap-1 text-muted">
                          <Timer size={14} />
                          {careLog.extractedData.duration}
                        </div>
                      )}
                      {careLog.extractedData.timeOfDay && (
                        <div className="flex items-center gap-1 text-muted">
                          <Clock size={14} />
                          {careLog.extractedData.timeOfDay}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="pt-4 border-t border-soft-neutral text-xs text-muted space-y-1">
              <div className="flex items-center gap-1">
                <Clock size={12} />
                Created: {formatTimestamp(careLog.createdAt)}
              </div>
              {careLog.updatedAt._seconds !== careLog.createdAt._seconds && (
                <div className="flex items-center gap-1">
                  <Clock size={12} />
                  Updated: {formatTimestamp(careLog.updatedAt)}
                </div>
              )}
              {careLog.inputType === 'voice' && (
                <div className="badge badge-secondary mt-2">
                  Voice Entry
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CareLogDetail;
